import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { formatCPFInput, formatPhoneInput } from '../utils/format';
import { ShieldCheck, ArrowRight } from 'lucide-react';

interface LoginPageProps {
  onNavigate: (path: string) => void;
  returnTo?: string;
}

export function LoginPage({ onNavigate, returnTo = '/conta' }: LoginPageProps) {
  const { user, login, register } = useAuth();
  const { showToast } = useToast();
  const [mode, setMode] = useState<'login' | 'register'>('login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [cpf, setCpf] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [requiresTotp, setRequiresTotp] = useState(false);
  const [loading, setLoading] = useState(false);

  // If already logged in, navigate to returnTo
  if (user) {
    onNavigate(returnTo);
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (mode === 'login') {
      const res = await login(email, password, totpCode || undefined);
      if (res.requiresTotp) {
        setRequiresTotp(true);
        showToast('info', 'Código TOTP Requerido', 'Digite o código de 6 dígitos do autenticador.');
      } else if (!res.success) {
        showToast('error', 'Falha no login', res.error);
      } else {
        showToast('success', 'Bem-vindo de volta', 'Login efetuado com sucesso.');
        onNavigate(returnTo);
      }
    } else {
      const res = await register({
        email,
        password,
        full_name: fullName,
        phone,
        cpf,
      });

      if (!res.success) {
        showToast('error', 'Falha no cadastro', res.error);
      } else {
        showToast('success', 'Cadastro realizado', 'Conta criada com sucesso.');
        onNavigate(returnTo);
      }
    }
    setLoading(false);
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="bg-surface p-8 rounded-xs border border-border-subtle shadow-elevated space-y-6">
        <div className="text-center space-y-2">
          <span className="font-serif text-2xl font-medium tracking-tight text-ink block">
            Aura Ateliê
          </span>
          <p className="text-xs text-ink-muted">
            {mode === 'login' ? 'Acesse seus pedidos, downloads e perfil' : 'Crie sua conta segura no ateliê'}
          </p>
        </div>

        {/* Tab switch */}
        <div className="flex border-b border-border-subtle text-xs">
          <button
            onClick={() => {
              setMode('login');
              setRequiresTotp(false);
            }}
            className={`flex-1 pb-3 font-medium uppercase tracking-wider transition-colors ${
              mode === 'login' ? 'border-b-2 border-primary text-ink' : 'text-ink-muted hover:text-ink'
            }`}
          >
            Entrar
          </button>
          <button
            onClick={() => {
              setMode('register');
              setRequiresTotp(false);
            }}
            className={`flex-1 pb-3 font-medium uppercase tracking-wider transition-colors ${
              mode === 'register' ? 'border-b-2 border-primary text-ink' : 'text-ink-muted hover:text-ink'
            }`}
          >
            Cadastrar
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Nome e Sobrenome"
                  className="w-full bg-background border border-border-subtle px-3 py-2.5 text-xs rounded-xs focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                  CPF (Criptografado) *
                </label>
                <input
                  type="text"
                  required
                  value={cpf}
                  onChange={(e) => setCpf(formatCPFInput(e.target.value))}
                  placeholder="000.000.000-00"
                  className="w-full bg-background border border-border-subtle px-3 py-2.5 text-xs rounded-xs focus:outline-none focus:border-primary font-mono"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
                  Telefone / WhatsApp
                </label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                  placeholder="(11) 98765-4321"
                  className="w-full bg-background border border-border-subtle px-3 py-2.5 text-xs rounded-xs focus:outline-none focus:border-primary font-mono"
                />
              </div>
            </>
          )}

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
              E-mail *
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              className="w-full bg-background border border-border-subtle px-3 py-2.5 text-xs rounded-xs focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-ink block mb-1">
              Senha *
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              className="w-full bg-background border border-border-subtle px-3 py-2.5 text-xs rounded-xs focus:outline-none focus:border-primary"
            />
          </div>

          {requiresTotp && (
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-primary block mb-1">
                Código de Autenticação em 2 Etapas (MFA)
              </label>
              <input
                type="text"
                required
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.trim())}
                placeholder="000000"
                className="w-full bg-background border-2 border-primary px-3 py-2.5 text-xs rounded-xs focus:outline-none font-mono text-center tracking-widest text-base"
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-ink text-ink-inverted py-3.5 rounded-xs text-xs uppercase tracking-widest font-medium hover:bg-primary transition-colors flex items-center justify-center gap-2 group shadow-subtle disabled:opacity-40"
          >
            {loading ? 'Aguarde...' : mode === 'login' ? 'Entrar na Conta' : 'Criar Conta'}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
        </form>

        <div className="pt-4 border-t border-border-subtle flex items-center justify-center gap-1.5 text-xs text-olive">
          <ShieldCheck className="w-4 h-4" />
          <span>Proteção criptográfica AES-256 e scrypt</span>
        </div>
      </div>
    </div>
  );
}
